// ── Serialized Story Quests ──────────────────────────────────────────
// 3 arcs × 5 episodes each. Every episode END is a hook: it names what
// happens next but never resolves in the same session. The arc simply
// waits if a day is missed — no expiry, no streak-shaming.
//
// Each episode.activityId is an EXISTING route in the app (game/lesson/
// quest surface). We never build new activities here — arcs are pure
// narrative wrapping around content that already exists.

export const STORY_ARCS = [
  {
    id: "lost-explorer",
    title: "The Lost Explorer",
    subject: "geography",
    hookLine: "A traveller left five clues across the continents. Can you follow the map?",
    color: "bg-sky-200",
    accent: "bg-sky-400",
    emoji: "🧭",
    badgeId: "arc-lost-explorer",
    badgeLabel: "Explorer's Compass",
    episodes: [
      {
        day: 1,
        title: "The first clue",
        narrativeIntro: "A folded note flutters onto the map. The explorer wrote: 'If you can name the continents, you can find where I went.'",
        activityId: "/first-quest",
        activityLabel: "Play First Quest",
        cliffhanger: "You spotted a jaguar in the corner of the map. Tomorrow: the jaguar remembers a country nobody named yet…",
      },
      {
        day: 2,
        title: "The jaguar's country",
        narrativeIntro: "The jaguar is waiting on a green continent. Match its flag home and it will show you the next clue.",
        activityId: "/games",
        activityQuery: "?category=geography",
        activityLabel: "Match Country to Continent",
        cliffhanger: "A cold wind blows across the map. Tomorrow: the trail leads to a frozen land — but who left these footprints?",
      },
      {
        day: 3,
        title: "Footprints on the ice",
        narrativeIntro: "The explorer's tracks lead north. Every step is a place on the globe — spin it and find where the wind is coldest.",
        activityId: "/globe",
        activityLabel: "Spin the globe",
        cliffhanger: "You find a torn page. It says: 'Learn the country of today and you'll learn where I sleep.' Tomorrow: today's country holds the answer.",
      },
      {
        day: 4,
        title: "Country of the day",
        narrativeIntro: "The explorer's page names a country you haven't visited yet. Open today's country and read its story.",
        activityId: "/globe",
        activityLabel: "Read today's country",
        cliffhanger: "One clue left. The explorer signs the last note: 'Meet me where every continent touches the sea.' Tomorrow: the final map.",
      },
      {
        day: 5,
        title: "Where every continent touches the sea",
        narrativeIntro: "Time to prove you know the map. Race the flags to the right continent — the explorer will be watching.",
        activityId: "/games",
        activityQuery: "?category=geography",
        activityLabel: "Play Continent Battle",
        cliffhanger: "You did it! The explorer leaves you their compass. A new page turns — a whole new arc is waiting…",
        finale: true,
      },
    ],
  },
  {
    id: "number-island",
    title: "Number Island",
    subject: "maths",
    hookLine: "An island only shows itself to children who solve its five puzzles.",
    color: "bg-amber-200",
    accent: "bg-amber-400",
    emoji: "🏝️",
    badgeId: "arc-number-island",
    badgeLabel: "Island Cartographer",
    episodes: [
      {
        day: 1,
        title: "The tide is a number",
        narrativeIntro: "The waves rise and fall in a pattern. Read them right and the sand will remember your name.",
        activityId: "/games/speed-tables",
        activityLabel: "Play Speed Tables",
        cliffhanger: "A shape drifts ashore in the sand. Tomorrow: the shape is the door — and it wants an answer.",
      },
      {
        day: 2,
        title: "The door on the beach",
        narrativeIntro: "The door has a maths puzzle carved on it. Solve one round and it will open just a crack.",
        activityId: "/games/dodge",
        activityLabel: "Play Dodge+",
        cliffhanger: "Behind the door is a jungle of numbers. Tomorrow: which path leads deeper?",
      },
      {
        day: 3,
        title: "Deeper into the jungle",
        narrativeIntro: "Every fruit on the trees has a number. Only the matching pairs are safe to pick.",
        activityId: "/games/match-pairs",
        activityLabel: "Play Match Pairs",
        cliffhanger: "You reach the middle of the island. A big stone tablet is waiting… Tomorrow: it wants a lesson, not a game.",
      },
      {
        day: 4,
        title: "The stone tablet",
        narrativeIntro: "The tablet whispers today's Maths Academy lesson. Learn it and the island trusts you a little more.",
        activityId: "/academy/maths",
        activityLabel: "Open Maths Academy",
        cliffhanger: "One puzzle stands between you and the island's secret. Tomorrow: the hop across the stones.",
      },
      {
        day: 5,
        title: "The hop across the stones",
        narrativeIntro: "The last stones are a number sequence. Answer to hop — one wrong step and you swim!",
        activityId: "/games/squishy",
        activityLabel: "Play Squishy Hop+",
        cliffhanger: "You've mapped the whole island. A treasure chest is yours — and a new arc is unlocking already…",
        finale: true,
      },
    ],
  },
  {
    id: "word-thief",
    title: "The Word Thief",
    subject: "english",
    hookLine: "Someone is stealing words from the town's storybook. Track them down before dinner disappears.",
    color: "bg-rose-200",
    accent: "bg-rose-400",
    emoji: "📖",
    badgeId: "arc-word-thief",
    badgeLabel: "Reader's Ribbon",
    episodes: [
      {
        day: 1,
        title: "The first stolen word",
        narrativeIntro: "The word for 'apple' is gone from the storybook. Only a spelling game can bring it back.",
        activityId: "/games/spell",
        activityLabel: "Play Spell It",
        cliffhanger: "Tiny footprints run across the page. Tomorrow: the thief drops a clue in a puzzle…",
      },
      {
        day: 2,
        title: "The clue in the puzzle",
        narrativeIntro: "The thief left rhyming clues. Build the words and the trail lights up.",
        activityId: "/reading",
        activityLabel: "Sound it out — Reading & Letters",
        cliffhanger: "The trail leads to the library. Tomorrow: today's English lesson has the missing chapter.",
      },
      {
        day: 3,
        title: "The missing chapter",
        narrativeIntro: "Open today's English lesson. The teacher left a note between two pages just for you.",
        activityId: "/learn-topics?subject=english",
        activityLabel: "Read today's English lesson",
        cliffhanger: "The thief is fast. Tomorrow: you need speed to catch them — sound out every word.",
      },
      {
        day: 4,
        title: "The chase",
        narrativeIntro: "Hear the word, spell it back before the thief runs off with it. Every hit is a rescued word.",
        activityId: "/spelling-bee",
        activityLabel: "Play Spelling Bee",
        cliffhanger: "The thief is cornered. Tomorrow: the last stand — face them in a spell-off.",
      },
      {
        day: 5,
        title: "The spell-off",
        narrativeIntro: "One last round of spelling and every word is returned. The storybook opens again.",
        activityId: "/games/duel/spell",
        activityLabel: "Play Spell-Off",
        cliffhanger: "Every word is safely home. The storybook slides you a new mystery — and a new arc opens…",
        finale: true,
      },
    ],
  },
  // Mixed-subject arc — Feb 2026 addition per user feedback that
  // story quests should span multiple subjects, not stay locked to one.
  // Each episode picks the activity that best fits the story beat, so
  // a single week weaves maths, english, geography and creative
  // exploration together into a single narrative thread.
  {
    id: "moonlit-map",
    title: "The Moonlit Map",
    subject: "mixed",
    hookLine: "A silver map appears each night — every subject leaves a mark. Follow the lot of them and the map will tell its secret.",
    color: "bg-violet-200",
    accent: "bg-violet-400",
    emoji: "🌙",
    badgeId: "arc-moonlit-map",
    badgeLabel: "Moonlit Cartographer",
    episodes: [
      {
        day: 1,
        title: "A country under the moon",
        narrativeIntro: "The map shines on ONE country tonight. Spin the globe and meet it — the map is drawing its first line.",
        activityId: "/first-quest",
        activityLabel: "Play First Quest",
        subject: "geography",
        cliffhanger: "The line on the map turns into numbers. Tomorrow: count them together with quick maths.",
      },
      {
        day: 2,
        title: "The numbers in the light",
        narrativeIntro: "The moon's numbers are a puzzle. Practise your times tables and the map will glow brighter.",
        activityId: "/games/speed-tables",
        activityLabel: "Play Speed Tables",
        subject: "maths",
        cliffhanger: "The map hums a word you don't know yet. Tomorrow: read it out loud together.",
      },
      {
        day: 3,
        title: "The word only the moon knows",
        narrativeIntro: "The moon whispers a spelling. Fill in the missing letters and the map paints a new island.",
        activityId: "/games/word-builder",
        activityLabel: "Play Word Builder",
        subject: "english",
        cliffhanger: "A shape blooms in the corner of the map. Tomorrow: colour it in your own way.",
      },
      {
        day: 4,
        title: "Colour the moon's shape",
        narrativeIntro: "Every explorer signs the moon-map in colour. Pick a scene and colour it — the map remembers every artist.",
        activityId: "/coloring",
        activityLabel: "Open Coloring",
        subject: "creative",
        cliffhanger: "The map is nearly whole. Tomorrow: prove you learnt every part — one quiz to finish the story.",
      },
      {
        day: 5,
        title: "The final quiz under the moon",
        narrativeIntro: "One quiz remains — a mix of everything the moon showed you. Answer well and the map is yours to keep.",
        activityId: "/quiz",
        activityLabel: "Take the Quiz",
        subject: "mixed",
        cliffhanger: "The moon's map is complete. It rolls itself up into a scroll and slides into your pocket — until the next new moon…",
        finale: true,
      },
    ],
  },
  // ── Jun 2026 · bigger stories ──
  {
    id: "cartographer-cipher",
    title: "The Cartographer's Cipher",
    subject: "mixed",
    hookLine: "A stranger called M hid a message in the world's data. Six chapters, every subject, one answer.",
    color: "bg-violet-200",
    accent: "bg-violet-400",
    emoji: "🗝️",
    badgeId: "arc-cartographer-cipher",
    badgeLabel: "Cartographer's Key",
    episodes: [
      { day: 1, title: "The parcel with no stamp", narrativeIntro: "An old atlas, a brass key, and a letter signed only 'M'. Twelve countries are marked with a number each.", activityId: "/globe", activityLabel: "Read today's country like a detective", subject: "geography",
        cliffhanger: "The numbers are latitudes — and every line runs through the Sahara. Next: a ratio written in a Kenyan margin." },
      { day: 2, title: "Shared fairly", narrativeIntro: "M has left a sum of money 'to be shared fairly between the two who find this' — in the ratio 2:3.", activityId: "/units/maths-pioneers-ratio-proportion-and-financial-maths", activityLabel: "Open the ratio unit", subject: "maths",
        cliffhanger: "Invisible ink appears: THE NILE REMEMBERS. Next: two sources from 1922 that don't agree." },
      { day: 3, title: "Two sources, one tomb", narrativeIntro: "A newspaper and a diary describe the same day in 1922 very differently. Which do you trust — and why?", activityId: "/units/history-pioneers-source-analysis-and-argument", activityLabel: "Analyse the sources", subject: "history",
        cliffhanger: "'Good,' writes M. 'Now the Nile takes you to where words are made.' Next: an unfinished letter that must convince a stranger." },
      { day: 4, title: "Where words are made", narrativeIntro: "M's persuasive letter argues every child should learn a second language — but the ending is blank.", activityId: "/units/english-explorers-persuasive-writing", activityLabel: "Finish the letter", subject: "english",
        cliffhanger: "The brass key grows warm. Next: sixty years of rainfall and a single circled column." },
      { day: 5, title: "Trend?", narrativeIntro: "Inside the box: rainfall data for twelve cities. M has written one word — 'trend?' — and left this year's row empty.", activityId: "/units/science-pioneers-climate-energy-and-sustainability", activityLabel: "Plot and predict", subject: "science",
        cliffhanger: "You give a range, not a number. The box clicks. One folded page remains — a map of your own town." },
      { day: 6, title: "The sixth story", narrativeIntro: "M was never hiding treasure. M was hiding a job: find the sixth story in the world's data and tell it to someone.", activityId: "/portfolio?add=project", activityLabel: "Start your first investigation", subject: "mixed",
        cliffhanger: "You write the title of your first investigation. The handwriting looks a little like M's…", finale: true },
    ],
  },
  {
    id: "pip-puffin",
    title: "Pip the Puffin Learns to Fly",
    subject: "mixed",
    hookLine: "A small puffin, a big blue world, and five little steps to brave.",
    color: "bg-amber-200",
    accent: "bg-amber-400",
    emoji: "🐧",
    badgeId: "arc-pip-puffin",
    badgeLabel: "Pip's Feather",
    episodes: [
      { day: 1, title: "The big blue world", narrativeIntro: "Pip stands on the cliff. The sea is far, far down. First we look at the whole wide world — then we fly.", activityId: "/first-quest", activityLabel: "Look at the whole world", subject: "geography",
        cliffhanger: "Pip's tummy rumbles. Tomorrow: Mum brings home silver fish to count." },
      { day: 2, title: "Counting fish", narrativeIntro: "One silver fish. Two silver fish. Three! Pip's tummy says 'more'.", activityId: "/snap", activityLabel: "Count with Math Snap", subject: "maths",
        cliffhanger: "Full tummies help with brave. Tomorrow: the seal next door says a funny word." },
      { day: 3, title: "Hej! Halló!", narrativeIntro: "The seal says 'Hej!' — that means hello where the seal comes from. Pip wants to say hello to everyone.", activityId: "/learn-countries", activityLabel: "Learn a hello", subject: "languages",
        cliffhanger: "Every animal says hello differently and means the same kind thing. Tomorrow: a grey day and a wobbly feeling." },
      { day: 4, title: "Breathe like the waves", narrativeIntro: "Pip's tummy feels tight. 'That's a worried feeling,' says Mum. 'Let's breathe like the waves.'", activityId: "/units/pshe-seedlings-feelings-and-friends", activityLabel: "Try the calm plan", subject: "pshe",
        cliffhanger: "The wobbly feeling gets smaller. Pip is bigger than it now. Tomorrow: the cliff, one more time." },
      { day: 5, title: "One… two… THREE!", narrativeIntro: "Pip has counted, said hello and breathed like the waves. Now Pip stands on the cliff again.", activityId: "/coloring", activityLabel: "Colour Pip flying", subject: "art",
        cliffhanger: "Pip lands right next to Mum. 'Again,' says Pip.", finale: true },
    ],
  },
  {
    id: "rainforest-radio",
    title: "The Rainforest Radio",
    subject: "mixed",
    hookLine: "A crackly old radio picks up voices from the Amazon. Five nights, five signals, one rescue.",
    color: "bg-emerald-200",
    accent: "bg-emerald-400",
    emoji: "📻",
    badgeId: "arc-rainforest-radio",
    badgeLabel: "Radio Ranger",
    episodes: [
      { day: 1, title: "Static and a song", narrativeIntro: "Grandpa's radio hasn't worked in years. Tonight it hums, crackles — and a bird sings out of it.", activityId: "/animals", activityLabel: "Find the singing bird", subject: "science",
        cliffhanger: "'Toucan,' you whisper. The radio answers with a word you don't know — and a river's roar. Tomorrow: coordinates." },
      { day: 2, title: "Two numbers, one river", narrativeIntro: "The voice on the radio reads two numbers over and over: 3 degrees south, 60 degrees west. Somewhere on the globe those lines cross.", activityId: "/globe", activityLabel: "Find where the lines cross", subject: "geography",
        cliffhanger: "Your finger lands on Brazil, deep in the Amazon. The voice says 'Obrigado' — thank you. Tomorrow: a message in a language you're about to learn." },
      { day: 3, title: "Obrigado, Amigo", narrativeIntro: "The radio speaks Portuguese. You learn hello, thank you, help — and hear the last word again and again.", activityId: "/country/brazil", activityLabel: "Learn Brazil's words", subject: "languages",
        cliffhanger: "'Ajuda' means help. Someone in the rainforest needs it. Tomorrow: the radio sends a puzzle only maths can open." },
      { day: 4, title: "The frequency puzzle", narrativeIntro: "'Tune to the answer,' says the voice. '7 times 8, then add 4.' The dial only goes to 100 — and there are more sums behind it.", activityId: "/times-tables-quest", activityLabel: "Tune the dial with times tables", subject: "maths",
        cliffhanger: "Sixty. The static clears completely. A ranger's voice: 'We've found the lost jaguar cub. Can you tell the world?' Tomorrow: your report." },
      { day: 5, title: "Tell the world", narrativeIntro: "The ranger asks you to write the news: what happened, where, and why the rainforest matters. Your words will go out on the radio.", activityId: "/portfolio?add=writing", activityLabel: "Write the radio report", subject: "english",
        cliffhanger: "You press the button. Your report crackles out across the trees. Somewhere, a toucan sings back.", finale: true },
    ],
  },
];

export function getArc(arcId) {
  return STORY_ARCS.find((a) => a.id === arcId) || null;
}
